/**
 * US-158: CCPA 45-Day Breach Notification Window Tracker
 * Task: T-158-003
 *
 * Tracks active CCPA violations that have breach notification deadlines.
 * Under CCPA § 1798.82, notification must be provided in the most expedient
 * time possible without unreasonable delay (statutory 45-day window).
 *
 * Statuses:
 * - ON_TRACK (>14 days)
 * - WARNING (7-14 days)
 * - CRITICAL (<=7 days)
 * - OVERDUE (past deadline)
 *
 * Testids:
 * - breach-tracker
 * - breach-item
 * - breach-countdown
 * - breach-status
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Violation, getViolations } from './engine';

export type BreachStatus = 'ON_TRACK' | 'WARNING' | 'CRITICAL' | 'OVERDUE';

export interface BreachCountdown {
  daysRemaining: number;
  status: BreachStatus;
}

/**
 * Calculates days remaining until the breach notification deadline
 * and assigns compliance urgency status.
 */
export function calculateBreachCountdown(
  deadlineStr: string,
  nowDate: Date = new Date()
): BreachCountdown {
  const deadline = new Date(deadlineStr);
  const now = new Date(nowDate);
  const diffMs = deadline.getTime() - now.getTime();
  const msPerDay = 1000 * 60 * 60 * 24;
  const daysRemaining = Math.ceil(diffMs / msPerDay);

  let status: BreachStatus;
  if (daysRemaining < 0) {
    status = 'OVERDUE';
  } else if (daysRemaining <= 7) {
    status = 'CRITICAL';
  } else if (daysRemaining <= 14) {
    status = 'WARNING';
  } else {
    status = 'ON_TRACK';
  }

  return { daysRemaining, status };
}

export interface BreachNotificationTrackerProps {
  violations?: Violation[];
  onRefresh?: () => void;
  className?: string;
}

export const BreachNotificationTracker: React.FC<BreachNotificationTrackerProps> = ({
  violations: propViolations,
  onRefresh,
  className = '',
}) => {
  const [internalViolations, setInternalViolations] = useState<Violation[]>([]);
  const [loading, setLoading] = useState<boolean>(propViolations === undefined);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (propViolations !== undefined) {
      setInternalViolations(propViolations);
      setLoading(false);
      return;
    }

    let isMounted = true;
    setLoading(true);
    getViolations()
      .then((data) => {
        if (isMounted) {
          setInternalViolations(data);
          setError(null);
        }
      })
      .catch((err: any) => {
        if (isMounted) {
          setError(err?.message || 'Failed to fetch breach violations');
        }
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [propViolations]);

  const activeViolations = propViolations !== undefined ? propViolations : internalViolations;

  // Track violations that have active deadlines
  const trackedItems = useMemo(() => {
    return activeViolations
      .filter((v) => Boolean(v.deadline))
      .map((v) => {
        const countdown = calculateBreachCountdown(v.deadline!);
        return {
          violation: v,
          countdown,
        };
      })
      .sort((a, b) => a.countdown.daysRemaining - b.countdown.daysRemaining);
  }, [activeViolations]);

  const statusBadgeClasses: Record<BreachStatus, string> = {
    ON_TRACK: 'bg-emerald-900/60 text-emerald-300 border-emerald-500/40',
    WARNING: 'bg-amber-900/60 text-amber-300 border-amber-500/40',
    CRITICAL: 'bg-rose-900/60 text-rose-300 border-rose-500/40',
    OVERDUE: 'bg-red-950 text-red-400 border-red-600 animate-pulse',
  };

  return (
    <div
      data-testid="breach-tracker"
      className={`bg-slate-900/90 border border-slate-800 rounded-xl p-5 shadow-lg ${className}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4 pb-3 border-b border-slate-800">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold text-slate-100">
              CCPA Breach Notification Tracker
            </h2>
            <span className="text-xs px-2 py-0.5 rounded-full bg-slate-800 text-slate-400 font-mono">
              45-Day Window
            </span>
          </div>
          <p className="text-xs text-slate-400 mt-0.5">
            California Civil Code § 1798.82 statutory notification countdown
          </p>
        </div>

        {onRefresh && (
          <button
            onClick={onRefresh}
            className="text-xs px-3 py-1.5 rounded bg-slate-800 hover:bg-slate-700 text-slate-300 transition-colors"
          >
            Refresh Deadlines
          </button>
        )}
      </div>

      {loading && (
        <div className="py-6 text-center text-sm text-slate-400">
          Loading breach notification windows...
        </div>
      )}

      {error && (
        <div className="py-3 px-4 rounded bg-rose-950/40 border border-rose-800/50 text-rose-300 text-xs mb-3">
          {error}
        </div>
      )}

      {!loading && !error && trackedItems.length === 0 && (
        <div className="py-8 text-center text-slate-400 text-sm bg-slate-950/40 rounded-lg border border-slate-800/60">
          <p className="font-medium text-slate-300">No Active Breach Deadlines</p>
          <p className="text-xs text-slate-500 mt-1">
            No active incidents require 45-day statutory notification at this time.
          </p>
        </div>
      )}

      {!loading && !error && trackedItems.length > 0 && (
        <div className="space-y-3">
          {trackedItems.map(({ violation, countdown }) => (
            <div
              key={violation.id}
              data-testid="breach-item"
              className="p-4 rounded-lg bg-slate-950/60 border border-slate-800/80 hover:border-slate-700 transition-colors flex flex-col md:flex-row md:items-center justify-between gap-4"
            >
              <div className="space-y-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-slate-200">
                    {violation.type}
                  </span>
                  <span
                    className={`text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded ${
                      violation.severity === 'CRITICAL'
                        ? 'bg-rose-950 text-rose-300 border border-rose-800'
                        : violation.severity === 'HIGH'
                        ? 'bg-amber-950 text-amber-300 border border-amber-800'
                        : 'bg-slate-800 text-slate-300'
                    }`}
                  >
                    {violation.severity}
                  </span>
                </div>
                <p className="text-xs text-slate-400">{violation.description}</p>
                <div className="text-[11px] text-slate-500 flex gap-4 pt-1">
                  <span>Detected: {new Date(violation.detectedAt).toLocaleDateString()}</span>
                  <span>Deadline: {new Date(violation.deadline!).toLocaleDateString()}</span>
                </div>
              </div>

              <div className="flex items-center gap-3 self-end md:self-center">
                <div className="text-right">
                  <div
                    data-testid="breach-countdown"
                    className="text-sm font-bold text-slate-100"
                  >
                    {countdown.daysRemaining < 0
                      ? `${Math.abs(countdown.daysRemaining)} days overdue`
                      : countdown.daysRemaining === 0
                      ? 'Due today (0 days remaining)'
                      : `${countdown.daysRemaining} days remaining`}
                  </div>
                  <div className="text-[10px] text-slate-500">Statutory Window</div>
                </div>

                <div
                  data-testid="breach-status"
                  className={`px-3 py-1 rounded text-xs font-semibold tracking-wide border uppercase ${statusBadgeClasses[countdown.status]}`}
                >
                  {countdown.status}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BreachNotificationTracker;
